/**
 * The admin portal (WP0.11): spaces, users, page templates, system info — and the design
 * baseline, which moved in here as a tab. Reversible writes act immediately with an undo
 * toast; the only confirm dialog is template deletion, which cannot be recreated afterwards.
 */

import { useCallback, useEffect, useState } from "react";

import type { AdminSpace, AdminUserInfo, SystemInfo } from "../../api";
import { api, ApiError } from "../../api";
import { useAuth } from "../../auth";
import { Dialog } from "../../components/Dialog";
import { EmptyState } from "../../components/EmptyState";
import { Badge, Button, Card, Field, Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";
import { useToast } from "../../components/toast";
import { DesignPage } from "../DesignPage";
import { AiSection } from "./AiSection";
import { PipelineSection } from "./PipelineSection";

const TABS = [
  { id: "utrymmen", label: "Utrymmen" },
  { id: "anvandare", label: "Användare" },
  { id: "ai", label: "AI" },
  { id: "pipeline", label: "Pipeline" },
  { id: "sidmallar", label: "Sidmallar" },
  { id: "system", label: "Systeminfo" },
  { id: "designgrund", label: "Designgrund" },
] as const;

type TabId = (typeof TABS)[number]["id"];

export function AdminPage() {
  const [tab, setTab] = useState<TabId>("utrymmen");

  return (
    <div className="grid gap-s6">
      <header className="grid gap-s2">
        <h1 className="nb-h1">Administration</h1>
        <p className="nb-body-muted">
          Utrymmen, konton och systemläge. Roller styrs i Entra-grupperna och läses vid
          inloggning — här visas de bara.
        </p>
      </header>

      <nav className="flex flex-wrap gap-s2" aria-label="Administrationsflikar">
        {TABS.map((item) => (
          <Button
            key={item.id}
            variant={tab === item.id ? "primary" : "ghost"}
            aria-current={tab === item.id ? "page" : undefined}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </Button>
        ))}
      </nav>

      {tab === "utrymmen" && <SpacesSection />}
      {tab === "anvandare" && <UsersSection />}
      {tab === "ai" && <AiSection />}
      {tab === "pipeline" && <PipelineSection />}
      {tab === "sidmallar" && <TemplatesSection />}
      {tab === "system" && <SystemSection />}
      {tab === "designgrund" && <DesignPage />}
    </div>
  );
}

function useApiErrorToast() {
  const showToast = useToast();
  return useCallback(
    (error: unknown, fallback: string) => {
      showToast({
        message: error instanceof ApiError ? error.message : fallback,
        tone: "fail",
      });
    },
    [showToast],
  );
}

// --- spaces ----------------------------------------------------------------------------------

function SpacesSection() {
  const showToast = useToast();
  const errorToast = useApiErrorToast();
  const [spaces, setSpaces] = useState<AdminSpace[] | null>(null);
  const [users, setUsers] = useState<AdminUserInfo[]>([]);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<"shared" | "private">("shared");
  const [ownerId, setOwnerId] = useState("");
  const [renaming, setRenaming] = useState<AdminSpace | null>(null);
  const [renameValue, setRenameValue] = useState("");

  useEffect(() => {
    void api
      .adminSpaces()
      .then(setSpaces)
      .catch((error) => {
        errorToast(error, "Utrymmena kunde inte hämtas.");
        setSpaces([]);
      });
    void api
      .adminUsers()
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [errorToast]);

  const create = async () => {
    if (!name.trim()) return;
    try {
      const created = await api.adminCreateSpace({
        name: name.trim(),
        kind,
        owner_id: kind === "private" ? ownerId || null : null,
      });
      setSpaces((current) =>
        [...(current ?? []), created].sort(
          (a, b) => b.kind.localeCompare(a.kind) || a.name.localeCompare(b.name, "sv"),
        ),
      );
      setName("");
      setOwnerId("");
      showToast({ message: `Utrymmet ”${created.name}” skapades.`, tone: "pass" });
    } catch (error) {
      errorToast(error, "Utrymmet kunde inte skapas.");
    }
  };

  const rename = async () => {
    if (!renaming || !renameValue.trim()) return;
    const previous = renaming.name;
    try {
      const updated = await api.adminRenameSpace(renaming.id, renameValue.trim());
      setSpaces((current) =>
        (current ?? []).map((space) => (space.id === updated.id ? updated : space)),
      );
      setRenaming(null);
      showToast({
        message: `”${previous}” heter nu ”${updated.name}”.`,
        undo: async () => {
          const reverted = await api.adminRenameSpace(updated.id, previous);
          setSpaces((current) =>
            (current ?? []).map((space) => (space.id === reverted.id ? reverted : space)),
          );
        },
      });
    } catch (error) {
      errorToast(error, "Namnbytet misslyckades.");
    }
  };

  if (spaces === null) return <SkeletonText lines={4} />;

  return (
    <div className="grid gap-s4">
      <Card className="grid gap-s4">
        <form
          className="grid gap-s4"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <span className="nb-overline">Nytt utrymme</span>
          <div className="grid gap-s4 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
            <Field label="Namn" value={name} onChange={(event) => setName(event.target.value)} />
            <label className="grid gap-s2">
              <span className="nb-overline border-0 pb-0">Typ</span>
              <select
                className="nb-field"
                value={kind}
                onChange={(event) => setKind(event.target.value as "shared" | "private")}
              >
                <option value="shared">Delat</option>
                <option value="private">Privat</option>
              </select>
            </label>
            {kind === "private" && (
              <label className="grid gap-s2">
                <span className="nb-overline border-0 pb-0">Ägare</span>
                <select
                  className="nb-field"
                  value={ownerId}
                  onChange={(event) => setOwnerId(event.target.value)}
                >
                  <option value="">Välj användare…</option>
                  {users.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.display_name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Button
              type="submit"
              variant="primary"
              disabled={!name.trim() || (kind === "private" && !ownerId)}
            >
              Skapa utrymme
            </Button>
          </div>
          <p className="nb-body-muted">
            Ett privat utrymme kan bara skapas åt någon som loggat in minst en gång.
          </p>
        </form>
      </Card>

      <Card className="grid gap-s4">
        <span className="nb-overline">Alla utrymmen</span>
        {spaces.length === 0 ? (
          <EmptyState
            title="Inga utrymmen ännu"
            description="Skapa det delade företagsutrymmet ovan så att sidor kan börja skrivas."
          />
        ) : (
          <ul className="grid gap-s2">
            {spaces.map((space) => (
              <li
                key={space.id}
                className="flex flex-wrap items-center justify-between gap-s3 border-b border-line pb-s2 last:border-b-0"
              >
                <div className="grid gap-s1">
                  <span className="nb-body">{space.name}</span>
                  <span className="nb-body-muted">
                    {space.kind === "shared" ? "Delat" : `Privat — ${space.owner_name ?? "?"}`}
                    {" · "}
                    <Num>{space.page_count}</Num> sidor
                  </span>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setRenaming(space);
                    setRenameValue(space.name);
                  }}
                >
                  Byt namn
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog
        open={renaming !== null}
        title="Byt namn på utrymmet"
        onClose={() => setRenaming(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setRenaming(null)}>
              Avbryt
            </Button>
            <Button variant="primary" disabled={!renameValue.trim()} onClick={() => void rename()}>
              Spara
            </Button>
          </>
        }
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void rename();
          }}
        >
          <Field
            label="Nytt namn"
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
          />
        </form>
      </Dialog>
    </div>
  );
}

// --- users -----------------------------------------------------------------------------------

function UsersSection() {
  const { auth } = useAuth();
  const showToast = useToast();
  const errorToast = useApiErrorToast();
  const [users, setUsers] = useState<AdminUserInfo[] | null>(null);

  const myId = auth.status === "signed_in" ? auth.user.id : null;

  useEffect(() => {
    void api
      .adminUsers()
      .then(setUsers)
      .catch((error) => {
        errorToast(error, "Användarna kunde inte hämtas.");
        setUsers([]);
      });
  }, [errorToast]);

  const setActive = async (target: AdminUserInfo, isActive: boolean) => {
    try {
      const updated = await api.adminSetUserActive(target.id, isActive);
      setUsers((current) => (current ?? []).map((u) => (u.id === updated.id ? updated : u)));
      showToast({
        message: isActive
          ? `${updated.display_name} kan logga in igen.`
          : `${updated.display_name} avaktiverades.`,
        undo: async () => {
          const reverted = await api.adminSetUserActive(target.id, !isActive);
          setUsers((current) => (current ?? []).map((u) => (u.id === reverted.id ? reverted : u)));
        },
      });
    } catch (error) {
      errorToast(error, "Ändringen kunde inte sparas.");
    }
  };

  if (users === null) return <SkeletonText lines={4} />;

  return (
    <Card className="grid gap-s4">
      <span className="nb-overline">Konton</span>
      <ul className="grid gap-s2">
        {users.map((account) => (
          <li
            key={account.id}
            className="flex flex-wrap items-center justify-between gap-s3 border-b border-line pb-s2 last:border-b-0"
          >
            <div className="grid gap-s1">
              <span className="nb-body">
                {account.display_name}
                {account.id === myId ? " (du)" : ""}
              </span>
              <span className="nb-body-muted">{account.email}</span>
            </div>
            <div className="flex items-center gap-s3">
              <Badge status={account.is_active ? "pass" : "fail"}>
                {account.is_active ? "Aktiv" : "Avaktiverad"}
              </Badge>
              <span className="nb-body-muted">
                {account.role === "admin" ? "Administratör" : "Medlem"}
              </span>
              <Button
                variant={account.is_active ? "danger" : "default"}
                disabled={account.id === myId}
                onClick={() => void setActive(account, !account.is_active)}
              >
                {account.is_active ? "Avaktivera" : "Aktivera"}
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <p className="nb-body-muted">
        En avaktiverad användare nekas vid nästa inloggning. Roller ändras i Entra-grupperna.
      </p>
    </Card>
  );
}

// --- page templates --------------------------------------------------------------------------

function TemplatesSection() {
  const showToast = useToast();
  const errorToast = useApiErrorToast();
  const [templates, setTemplates] = useState<{ id: string; name: string }[] | null>(null);
  const [confirming, setConfirming] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    void api
      .pageTemplates()
      .then(setTemplates)
      .catch((error) => {
        errorToast(error, "Mallarna kunde inte hämtas.");
        setTemplates([]);
      });
  }, [errorToast]);

  const remove = async () => {
    if (!confirming) return;
    try {
      await api.removePageTemplate(confirming.id);
      setTemplates((current) => (current ?? []).filter((t) => t.id !== confirming.id));
      showToast({ message: `Mallen ”${confirming.name}” togs bort.`, tone: "pass" });
    } catch (error) {
      errorToast(error, "Mallen kunde inte tas bort.");
    } finally {
      setConfirming(null);
    }
  };

  if (templates === null) return <SkeletonText lines={3} />;

  return (
    <Card className="grid gap-s4">
      <span className="nb-overline">Sidmallar</span>
      {templates.length === 0 ? (
        <EmptyState
          title="Inga sidmallar"
          description="Mallar skapas från en färdig sida: öppna sidan under Sidor och välj ”Spara som mall”."
        />
      ) : (
        <ul className="grid gap-s2">
          {templates.map((template) => (
            <li
              key={template.id}
              className="flex items-center justify-between gap-s3 border-b border-line pb-s2 last:border-b-0"
            >
              <span className="nb-body">{template.name}</span>
              <Button variant="danger" onClick={() => setConfirming(template)}>
                Ta bort
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* A deleted template cannot be recreated (its source page may be gone), so this is the
          one irreversible action in the portal — hence a dialog, not an undo toast. */}
      <Dialog
        open={confirming !== null}
        title="Ta bort mallen?"
        subtitle={
          confirming
            ? `”${confirming.name}” försvinner för alla. Detta går inte att ångra.`
            : undefined
        }
        onClose={() => setConfirming(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(null)}>
              Avbryt
            </Button>
            <Button variant="danger" onClick={() => void remove()}>
              Ta bort mallen
            </Button>
          </>
        }
      >
        <p className="nb-body">Sidor som redan skapats från mallen påverkas inte.</p>
      </Dialog>
    </Card>
  );
}

// --- system ----------------------------------------------------------------------------------

function SystemSection() {
  const errorToast = useApiErrorToast();
  const [info, setInfo] = useState<SystemInfo | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void api
      .adminSystem()
      .then(setInfo)
      .catch((error) => {
        errorToast(error, "Systeminformationen kunde inte hämtas.");
        setFailed(true);
      });
  }, [errorToast]);

  if (failed) {
    return (
      <EmptyState
        title="Ingen systeminfo"
        description="Hämtningen misslyckades — försök igen om en stund."
      />
    );
  }
  if (info === null) return <SkeletonText lines={4} />;

  const rows: [string, string][] = [
    ["Version", info.version],
    ["Miljö", info.environment],
    ["Inloggning", info.auth_mode === "oidc" ? "Entra ID" : "Lokal (utveckling)"],
    ["Modellrouter", info.model_router_mode],
    ["Migrationsläge", info.migration_revision ?? "okänt"],
  ];
  const counts: [string, number][] = [
    ["Användare", info.users],
    ["Utrymmen", info.spaces],
    ["Sidor", info.pages],
    ["Fångster", info.captures],
  ];

  return (
    <div className="grid gap-s4">
      <Card className="grid gap-s4">
        <span className="nb-overline">Drift</span>
        <dl className="grid gap-s2">
          {rows.map(([label, value]) => (
            <div
              key={label}
              className="flex justify-between gap-s3 border-b border-line pb-s2 last:border-b-0"
            >
              <dt className="nb-body-muted">{label}</dt>
              <dd className="nb-body">{value}</dd>
            </div>
          ))}
        </dl>
      </Card>
      <Card className="grid gap-s4">
        <span className="nb-overline">Innehåll</span>
        <dl className="grid grid-cols-2 gap-s4 sm:grid-cols-4">
          {counts.map(([label, value]) => (
            <div key={label} className="grid gap-s1">
              <dt className="nb-body-muted">{label}</dt>
              <dd>
                <Num className="nb-h2">{value}</Num>
              </dd>
            </div>
          ))}
        </dl>
      </Card>
    </div>
  );
}
